import assert from 'assert';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CustomContextPackage, makeReactContext} from 'lib/makeReactContext';
import {MethodEditorTab} from 'procedures/design/method-config/MethodConfigConstants';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';

import {
  MaterialFlow,
  MethodNode,
  ProcedureFlowchart,
  ProcedureMetadata,
  ProcedureTemplateCollectionOwner,
} from './ProcedureDomainTypes';

export enum BlockingSaveObjectSource {
  Procedure = 'procedure', // Useful when the entire procedure is being saved
  Method = 'method',
  Step = 'step',
  Parameter = 'parameter',
  MaterialEdge = 'material_edge',
  TestDefinition = 'test_definition',
}
export enum BlockingSaveOperation {
  Create = 'create',
  Update = 'update',
  Duplicate = 'duplicate',
  OpenHistoryPanel = 'open_history_panel',
  SaveOnBeginCreateStudy = 'save_on_begin_create_study',
  SaveOnPublishOperation = 'save_on_publish_operation',
  SaveUnsubmittedOnExit = 'save_unsubmitted_on_exit',
}

interface PeristenceInfoBase {
  cloneStepCidToDocumentVersionIdMap?: Record<string, string>;
}
interface PeristenceInfoForBlockingSave extends PeristenceInfoBase {
  blockingSaveObjectSource: BlockingSaveObjectSource;
  blockingSaveOperation: BlockingSaveOperation;
}

// Used for autosaves + other non-blocking persistence operations (ex: duplicate procedure)
interface PeristenceInfoForAutoSave extends PeristenceInfoBase {
  blockingSaveObjectSource?: never;
  blockingSaveOperation?: never;
}

/**
 * Optional object containing information about the flowchart when saving. This object is specifically
 * used for a single patch call and not metadata that is persisted.
 */
export type PersistenceInfo = PeristenceInfoForBlockingSave | PeristenceInfoForAutoSave;

export type PreSaveImmediatelyMiddleware = (method: MethodNode) => MethodNode;

export interface ProcedurePageContextValue {
  isProcedureDirty: boolean;
  /**
   * Use this function when the user takes an action which requires an immediate server call.
   * IMPORTANT NOTES:
   *   - You must make changes to the procedure inside the callback, not before invoking this function!
   *   - You must make changes against the provided flowchart only. Do not use whatever flowchart you have
   *       from props or cached locally.
   *
   * GOOD:
   *    saveImmediately((latestFlowchart) => {applyUpdates(latestFlowchart, updates)});
   *
   * BAD:
   *    applyUpdates(props.flowchart, updates);
   *    saveImmediately(noop);
   */
  saveImmediately: (
    applyChangesAfterInflightSaves: (latestFlowchart: ProcedureFlowchart) => PersistenceInfo,
    overlayMessage?: string,
    shouldBubbleUpErrors?: boolean
  ) => Promise<ProcedureMetadata>;
  // Downstream components can patch into all calls to `saveImmediately`
  registerPreSaveImmediatelyMiddleware: (methodCid: string, fn: PreSaveImmediatelyMiddleware) => string;
  unregisterPreSaveImmedatelyMiddleware: (id: string) => void;
  isSaving: boolean;
  selectedMethodCid: string | null;
  setSelectedMethodCid: (cid: string | null) => void;
  // This value is used to track when one of the tabs in the method editor is in a *modal* editing state.
  // Using it allows us to warn the user before discarding unsaved changes in that tab.
  isEditingMethodTab: boolean;
  setIsEditingMethodTab: (isEditing: boolean) => void;

  currentTab: MethodEditorTab;
  /** Directly set current tab. Generally, we want to run the setCurrentTab function defined in MethodEditor
   * instead, which includes a check for unsaved changes. */
  dangerouslySetCurrentTab: (newTab: MethodEditorTab) => void;

  /**
   * Downstream components can turn off autosaving temporarily
   */
  shouldBlockAutosave: boolean;
  setShouldBlockAutosave: (shouldBlock: boolean) => void;

  idToCidMap: Record<string, string>;
  resetDraftFlowchartChanges: () => void;
  materialFlows: Array<MaterialFlow>;
  accessLevel: null | ProcedureAccessLevel;
  procedureId: string;
  procedureType: ProcedureType;
  procedureTemplateCollectionOwner: ProcedureTemplateCollectionOwner;
}

/**
 * A version of the ProcedurePageContext that allows for use without a surrounding Provider. This cannot be
 * used directly and should only be done via useNullableProcedurePageContext.
 */
const NullableProcedurePageContext = makeReactContext<ProcedurePageContextValue | null>(
  () => null,
  'ProcedurePageContext'
);

/** Context containing info about a Procedure. Throws an error when trying to access outside of a set context. */
export const ProcedurePageContext: CustomContextPackage<ProcedurePageContextValue> = {
  ...NullableProcedurePageContext,
  useContext: () => {
    const context = NullableProcedurePageContext.useContext();
    assert(
      context,
      'Unable to run without a ProcedurePageContext. In tests, use `mountComponentWithProcedurePageContext`'
    );
    return context;
  },
};

/** The same as ProcedurePageContext.useContext(), except that it doesn't require the context be provided. */
export function useNullableProcedurePageContext(): ProcedurePageContextValue | null {
  return NullableProcedurePageContext.useContext();
}
